import React, { useMemo } from "react";
import FullCalendar from "@fullcalendar/react";
import dayGridPlugin from "@fullcalendar/daygrid";
import timeGridPlugin from "@fullcalendar/timegrid";
import interactionPlugin from "@fullcalendar/interaction";
import bootstrapPlugin from "@fullcalendar/bootstrap";
import "@fullcalendar/common/main.css";
import "@fullcalendar/daygrid/main.css";
import "@fullcalendar/timegrid/main.css";
import "@fullcalendar/bootstrap/main.css";

function isValidIanaTimeZone(tz) {
  if (!tz || typeof tz !== "string") return false;
  try {
    Intl.DateTimeFormat(undefined, { timeZone: tz.trim() });
    return true;
  } catch {
    return false;
  }
}

const PALETTE = ["#198754", "#0d6efd", "#6f42c1", "#fd7e14", "#d63384", "#0dcaf0", "#6610f2"];

function colorForPractitioner(practitionerId) {
  if (!practitionerId) return "#6c757d";
  let h = 0;
  const s = String(practitionerId);
  for (let i = 0; i < s.length; i += 1) h = (h * 31 + s.charCodeAt(i)) >>> 0;
  return PALETTE[h % PALETTE.length];
}

function practitionerLabel(slot) {
  const name = slot.practitioner?.displayName;
  if (name) return name;
  if (slot.practitioner) return "Practitioner";
  return "Unassigned";
}

function slotsToEvents(slots) {
  return slots.map((slot) => {
    const booked = slot.status === "booked";
    const external = slot.slotSource === "external_inbound";
    const plabel = practitionerLabel(slot);
    const pid = slot.practitioner?._id || slot.practitioner;

    let backgroundColor = "#198754";
    if (booked) backgroundColor = "#0d6efd";
    else if (external) backgroundColor = "#20c997";
    else if (pid) backgroundColor = colorForPractitioner(pid);

    let title = booked ? "Booked" : external ? "External sync" : "Platform";
    title = `${title} · ${plabel}`;

    return {
      id: String(slot._id),
      title,
      start: slot.startAt,
      end: slot.endAt,
      backgroundColor,
      borderColor: external ? "#fd7e14" : undefined,
      extendedProps: { slot },
    };
  });
}

/**
 * Week/day/month views with slot coloring; loads visible range via datesSet.
 */
const SlotAvailabilityCalendar = ({
  slots,
  slotsLoading,
  timeZone,
  onDatesSet,
  onSelectSlot,
  onEventClick,
}) => {
  const events = useMemo(() => slotsToEvents(slots), [slots]);
  const fcTimeZone = isValidIanaTimeZone(timeZone) ? timeZone.trim() : undefined;

  return (
    <div className="clinic-tc-calendar-wrap position-relative">
      {slotsLoading ? (
        <div
          className="position-absolute w-100 h-100 d-flex align-items-center justify-content-center"
          style={{ background: "rgba(255,255,255,0.6)", zIndex: 2 }}
        >
          <div className="d-flex align-items-center text-soft">
            <div className="spinner-border spinner-border-sm text-primary mr-2" role="status" />
            Loading slots…
          </div>
        </div>
      ) : null}
      <FullCalendar
        plugins={[dayGridPlugin, timeGridPlugin, interactionPlugin, bootstrapPlugin]}
        initialView="timeGridWeek"
        themeSystem="bootstrap"
        headerToolbar={{
          left: "prev,next today",
          center: "title",
          right: "dayGridMonth,timeGridWeek,timeGridDay",
        }}
        height={640}
        slotDuration="00:15:00"
        snapDuration="00:15:00"
        allDaySlot={false}
        weekends
        timeZone={fcTimeZone || "local"}
        events={events}
        datesSet={(info) => {
          onDatesSet(info.start, info.end);
        }}
        selectable
        selectMirror
        unselectAuto
        selectMinDistance={5}
        selectAllow={(selectInfo) => {
          if (!selectInfo?.start) return false;
          const viewType = selectInfo.view?.type;
          if (viewType === "dayGridMonth") return false;
          const t = selectInfo.start.getTime();
          if (Number.isNaN(t)) return false;
          return t > Date.now() - 60 * 1000;
        }}
        select={(info) => {
          onSelectSlot(info.start, info.end);
        }}
        eventClick={(info) => {
          info.jsEvent.preventDefault();
          const slot = info.event.extendedProps.slot;
          if (slot) onEventClick(slot);
        }}
        eventDisplay="block"
        nowIndicator
      />
      <p className="text-soft small mt-2 mb-0">
        <span className="d-inline-block me-3">
          <span className="badge" style={{ background: "#198754" }}>
            &nbsp;
          </span>{" "}
          Platform (color by practitioner)
        </span>
        <span className="d-inline-block me-3">
          <span className="badge" style={{ background: "#6c757d" }}>
            &nbsp;
          </span>{" "}
          Unassigned
        </span>
        <span className="d-inline-block me-3">
          <span className="badge" style={{ background: "#20c997", border: "2px solid #fd7e14" }}>
            &nbsp;
          </span>{" "}
          External sync
        </span>
        <span className="d-inline-block">
          <span className="badge" style={{ background: "#0d6efd" }}>
            &nbsp;
          </span>{" "}
          Booked
        </span>
      </p>
    </div>
  );
};

export default SlotAvailabilityCalendar;
